import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { type PasteGroup } from '../clipboard/utils';
import { createGroupsAfterPaste } from './commands';
import { type Command } from './types';

export type DuplicateByDragSession = {
  clones: AppNode[];
  groups: PasteGroup[];
};

export function startDuplicateByDrag({ clones, groups }: DuplicateByDragSession): void {
  useEditCanvasStore.getState().addNodes(clones);

  for (const group of groups) {
    const memberIds = group.memberIndices.map((index) => clones[index].id);
    useEditCanvasStore.getState().setGroupId(memberIds, crypto.randomUUID());
  }
}

export function buildDuplicateByDragCommand(
  { clones, groups }: DuplicateByDragSession,
  nodes: { id: string; position: { x: number; y: number } }[],
  previousPositions: { id: string; position: { x: number; y: number } }[],
): Command {
  const cloneIds = new Set(clones.map((clone) => clone.id));
  const newPosMap = new Map(nodes.map((node) => [node.id, node.position]));
  const prevPosMap = new Map(previousPositions.map((entry) => [entry.id, entry.position]));

  const movedOriginals = [...newPosMap.entries()]
    .filter(([id]) => !cloneIds.has(id))
    .map(([id, position]) => ({ id, position }));

  const restoredOriginals = [...prevPosMap.entries()]
    .filter(([id]) => !cloneIds.has(id))
    .map(([id, position]) => ({ id, position }));

  return {
    execute: () => {
      const { nodes: storeNodes } = useEditCanvasStore.getState();
      const hasClones = storeNodes.some((node) => cloneIds.has(node.id));

      if (movedOriginals.length > 0) {
        useEditCanvasStore.getState().moveNodes(movedOriginals);
      }

      if (!hasClones) {
        useEditCanvasStore.getState().addNodes(clones);

        if (groups.length > 0) {
          const groupLocalIds: string[][] = [];

          for (const group of groups) {
            const memberIds = group.memberIndices.map((index) => clones[index].id);
            groupLocalIds.push(memberIds);
            useEditCanvasStore.getState().setGroupId(memberIds, crypto.randomUUID());
          }

          createGroupsAfterPaste(groupLocalIds);
        }
      }
    },
    undo: () => {
      useEditCanvasStore.getState().removeNodes(clones.map((clone) => clone.id));

      if (restoredOriginals.length > 0) {
        useEditCanvasStore.getState().moveNodes(restoredOriginals);
      }
    },
  };
}

export function commitDuplicateByDrag(
  execute: (command: Command) => void,
  session: DuplicateByDragSession,
  nodes: { id: string; position: { x: number; y: number } }[],
  previousPositions: { id: string; position: { x: number; y: number } }[],
): void {
  execute(buildDuplicateByDragCommand(session, nodes, previousPositions));

  if (session.groups.length > 0) {
    const groupLocalIds = session.groups.map((group) => group.memberIndices.map((index) => session.clones[index].id));
    createGroupsAfterPaste(groupLocalIds);
  }
}
